import { FOCUS_RING, Icon, Pills } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { ObjectivePillar } from '@/types/objectives';

import { DETAIL_SECTION_TITLE, GLASS_SURFACE } from '@/components/objectives/objectiveStyles';
import PillarIcon from '@/components/objectives/PillarIcon';
import PillarStatusMention from '@/components/objectives/PillarStatusMention';
import { derivePillarTileGradient } from '@/components/objectives/pillarSurface';
import { resolveCompanyValueIconBackground } from '@/utils/companyValueIconBackground';
import { getLocalizedText } from '@/utils/localization';

interface ObjectiveSkillsPillarsCardProps {
    /** Empty when the objective is aligned with no pillar; the pillar block is then left out. */
    pillars: ObjectivePillar[];
    /** Empty unless the objective is individual and its owner still holds the competencies. */
    competencyNames: string[];
    onPillarClick: (pillarId: string) => void;
}

/**
 * "Compétences et piliers stratégiques": the impact line, the pillars the objective feeds, and
 * the competencies it works on. The impact line follows the pillars.
 */
export default function ObjectiveSkillsPillarsCard({
    pillars,
    competencyNames,
    onPillarClick,
}: ObjectiveSkillsPillarsCardProps) {
    const { t } = useTranslation();
    const hasPillars = pillars.length > 0;

    if (!hasPillars && competencyNames.length === 0) {
        return null;
    }

    return (
        <div
            className={`obj-skills-card ${GLASS_SURFACE} w-full max-w-[774px]`}
            data-testid={'objective-detail-skills-pillars'}
        >
            <div className={'obj-skills-gap flex flex-col'}>
                <div className={'obj-skills-gap flex flex-col'}>
                    <h3 className={DETAIL_SECTION_TITLE}>
                        {t('objectives.detail.skillsAndPillars')}
                    </h3>

                    {hasPillars && (
                        <>
                            <div className={'flex items-start gap-3'}>
                                <Icon
                                    name={'users-profile-circle'}
                                    size={24}
                                    colorClassName={'text-navy'}
                                    strokeWidth={1.5}
                                />

                                <div className={'flex flex-col gap-1.5'}>
                                    <h4 className={'text-navy text-[20px] font-medium'}>
                                        {t('objectives.detail.yourImpact')}
                                    </h4>
                                    <p className={'text-obj-muted text-[14px] font-medium'}>
                                        {t('objectives.detail.impactDesc', {
                                            count: pillars.length,
                                        })}
                                    </p>
                                </div>
                            </div>

                            <div
                                className={'bg-obj-stepper h-px w-full'}
                                aria-hidden
                            />
                        </>
                    )}
                </div>

                {hasPillars && (
                    <div className={'flex flex-col gap-3'}>
                        <h4 className={'text-navy text-[14px] font-medium'}>
                            {t('objectives.detail.strategicPillars')}
                        </h4>

                        {/* A click anywhere on a tile opens its pillar's window; the keyboard and
                            assistive technology reach it through the button on the pillar name,
                            inside the heading so it stays a heading. */}
                        {pillars.map((pillar) => (
                            <div
                                key={pillar.id}
                                onClick={() => onPillarClick(pillar.id)}
                                className={`${GLASS_SURFACE} w-full cursor-pointer px-6 py-6 text-left shadow-sm hover:bg-white/60`}
                                data-testid={`objective-detail-pillar-link-${pillar.id}`}
                            >
                                <div className={'flex items-center gap-3'}>
                                    <div
                                        className={
                                            'flex h-[65px] w-[65px] flex-shrink-0 items-center justify-center rounded-[10px] shadow-sm'
                                        }
                                        style={{
                                            background: derivePillarTileGradient(
                                                resolveCompanyValueIconBackground(
                                                    pillar.icon,
                                                    pillar.iconBackground,
                                                ),
                                            ),
                                        }}
                                        aria-hidden
                                    >
                                        <PillarIcon
                                            icon={pillar.icon}
                                            size={40}
                                        />
                                    </div>

                                    <div className={'flex min-w-0 flex-col gap-0.5'}>
                                        <h5
                                            className={
                                                'text-navy text-[20px] leading-[32px] font-medium tracking-[0.4px]'
                                            }
                                        >
                                            <button
                                                type={'button'}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onPillarClick(pillar.id);
                                                }}
                                                aria-haspopup={'dialog'}
                                                className={`${FOCUS_RING} text-left`}
                                                data-testid={`objective-detail-pillar-name-${pillar.id}`}
                                            >
                                                {getLocalizedText(pillar.name)}
                                            </button>
                                        </h5>
                                        <div className={'flex flex-wrap items-center gap-2'}>
                                            <p className={'text-obj-muted text-[14px] font-medium'}>
                                                {t('objectives.detail.pillar')}
                                            </p>

                                            <PillarStatusMention
                                                status={pillar.status}
                                                dataTestId={`objective-detail-pillar-inactive-${pillar.id}`}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {competencyNames.length > 0 && (
                    <div
                        className={'flex flex-col gap-3'}
                        data-testid={'objective-detail-competencies'}
                    >
                        <h4 className={'text-navy text-[14px] font-medium'}>
                            {t('objectives.detail.competencies')}
                        </h4>

                        <div className={'flex flex-wrap gap-2'}>
                            {/* `violet` is the deepest purple the library's `Pills` ships; the
                                reference uses a darker one still, which would need a variant of
                                its own rather than a fill forced over the component's. */}
                            {competencyNames.map((name) => (
                                <Pills
                                    key={name}
                                    variant={'violet'}
                                    size={'tinted'}
                                >
                                    {name}
                                </Pills>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
